<template>
  <div class="tabs-list" :class="{ 'tabs-list--design-new': design === 'new', 'tabs-list--refined': refined, 'tabs-list--medium': weight === 'medium' }">
    <ul class="tabs-list__body" :role="design === 'new' ? 'tablist' : null">
      <li v-for="tab in visibleTabs" :key="tab.id || tab.name || tab.value" class="tabs-list__item">
        <button
          v-if="hasValue(tab)"
          type="button"
          class="tabs-list__link tabs-list__button"
          :class="{ 'tabs-list__link--active': value === tab.value }"
          :role="design === 'new' ? 'tab' : null"
          :aria-selected="design === 'new' ? String(value === tab.value) : null"
          @click="$emit('input', tab.value)"
        >
          <i v-if="tab.icon" class="tabs-list__icon" :class="tab.icon" aria-hidden="true" />
          <span>{{ tab.label }}</span>
          <span
            v-if="hasCount(tab)"
            class="tabs-list__count"
            :class="countClass(tab)"
          >
            {{ tab.count }}
          </span>
        </button>
        <router-link
          v-else
          :to="{ name: tab.name }"
          :exact-path="design === 'new'"
          exact-active-class="tabs-list__link--active"
          class="tabs-list__link"
          :role="design === 'new' ? 'tab' : null"
          :aria-selected="design === 'new' ? String($route.name === tab.name) : null"
        >
          <i v-if="tab.icon" class="tabs-list__icon" :class="tab.icon" aria-hidden="true" />
          <span>{{ tab.label }}</span>
          <span
            v-if="hasCount(tab)"
            class="tabs-list__count"
            :class="countClass(tab)"
          >
            {{ tab.count }}
          </span>
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script>
import { allowsAccess } from '@/utils/permissionAccess'

export default {
  name: 'AppTabs',
  props: {
    tabs: Array,
    value: {
      type: [String, Number],
      default: null
    },
    design: {
      type: String,
      default: 'legacy',
      validator: value => ['legacy', 'new'].includes(value)
    },
    refined: Boolean,
    weight: {
      type: String,
      default: 'default',
      validator: value => ['default', 'medium'].includes(value)
    }
  },
  computed: {
    visibleTabs() {
      return (this.tabs || []).filter(tab => !tab.access || allowsAccess(tab.access, this.$store.state.userData, this.$store.getters['permissions/can']))
    }
  },
  methods: {
    hasValue(tab) {
      return Object.prototype.hasOwnProperty.call(tab, 'value')
    },
    hasCount(tab) {
      return Object.prototype.hasOwnProperty.call(tab, 'count') && tab.count !== null
    },
    countClass(tab) {
      return tab.countTheme ? `tabs-list__count--${tab.countTheme}` : null
    }
  }
}
</script>

<style lang="sass" scoped>
.tabs-list
  margin-bottom: 20px
  padding-bottom: 1px
  border-bottom: 1px solid #c7cfdd
  overflow-x: auto
  overflow-y: hidden
  scrollbar-color: $color-theme #fff
  scrollbar-width: thin

  &::-webkit-scrollbar
    width: 4px
    transition: width 0.3s

  &::-webkit-scrollbar-track
    border-radius: 5px
    background-color: #ffff

  &::-webkit-scrollbar-thumb
    border-radius: 5px
    background-color: $color-theme

.tabs-list__body
  display: flex

.tabs-list__item
  flex-shrink: 0
  margin-right: 30px

  &:last-child
    margin-right: 0

.tabs-list__link
  position: relative
  display: block
  padding-bottom: 10px
  color: $color-text-base
  font-size: 16px
  font-weight: 600
  text-decoration: none
  transition: color 0.3s

  &:after
    content: ''
    position: absolute
    left: 0
    bottom: -1px
    width: 100%
    height: 3px
    background-color: $color-theme
    opacity: 0
    transition: opacity 0.3s

  &:hover
    color: $color-theme

.tabs-list__count
  display: inline-flex
  min-width: 20px
  height: 20px
  align-items: center
  justify-content: center
  margin-left: 6px
  padding: 0 6px
  border-radius: 10px
  background-color: #edf1f7
  color: $color-text-base
  font-size: 12px
  line-height: 20px

.tabs-list__icon
  margin-right: 8px
  font-size: 16px

.tabs-list__link--active
  pointer-events: none

  &:after
    opacity: 1

.tabs-list__button
  padding-top: 0
  border: 0
  background: transparent
  cursor: pointer

.tabs-list--design-new
  margin-bottom: $ui-space-24
  padding-bottom: 0
  border-bottom: 0
  scrollbar-color: var(--ui-accent, #{$ui-blue}) $ui-surface

  .tabs-list__body
    gap: $ui-space-8

  .tabs-list__item
    margin-right: 0

  .tabs-list__link
    display: flex
    align-items: center
    min-height: 46px
    padding: 0 $ui-space-16
    border: 1px solid $ui-line-strong
    border-radius: $ui-tab-radius
    background-color: $ui-surface
    color: $ui-text
    font-family: $ui-font-body
    font-size: $ui-font-size-body
    font-weight: 500
    box-shadow: $ui-card-shadow
    transition: color .2s ease, border-color .2s ease, background-color .2s ease

  .tabs-list__count
    background-color: var(--ui-accent-soft, #{$ui-blue-soft})
    color: var(--ui-accent, #{$ui-blue})

  .tabs-list__link
    &:hover
      border-color: var(--ui-accent, #{$ui-blue})
      color: var(--ui-accent, #{$ui-blue})
      text-decoration: none

    &:focus-visible
      outline: 3px solid var(--ui-accent-soft, #{rgba($ui-blue, .18)})
      outline-offset: 2px

    &::after
      display: none

  .tabs-list__link--active
    border-color: var(--ui-accent, #{$ui-blue})
    background-color: var(--ui-accent-soft, #{$ui-blue-soft})
    color: var(--ui-accent, #{$ui-blue})

.tabs-list--design-new.tabs-list--refined
  margin-bottom: $ui-space-12

  .tabs-list__link
    min-height: 40px

  .tabs-list__link--active
    border-color: var(--ui-accent, #{$ui-blue})
    background-color: var(--ui-accent-soft, #{$ui-blue-soft})
    color: $ui-ink
    font-weight: 700

.tabs-list--design-new.tabs-list--medium
  .tabs-list__link--active
    font-weight: 500

.tabs-list__count.tabs-list__count--danger
  background-color: #dd4e4e
  color: #fff
</style>
